import React, { useState, useEffect,useContext } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { Button, Textarea, Select } from "@windmill/react-ui";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { GoAlert } from "react-icons/go";
import { IoArrowBack } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import Error from "../components/form/Error";
import InputArea from "../components/form/InputArea";
import LabelArea from "../components/form/LabelArea";
import LabelTextarea from "../components/form/LabelTextarea";
import LogoUploader from "../components/image-uploader/LogoUploader";
import timezones from "../utils/timezones";
//import Logo from "../assets/img/logo/towne-main-logo.svg";
import LightLogo from "../assets/img/logo/Town-logo-lt.svg";
import DarkLogo from "../assets/img/logo/Town-logo-dark.svg";
import validations from "../utils/validations";

import { WindmillContext } from "@windmill/react-ui";

import { getError, descriptionCharLimitClass } from "../utils/helpers";
import {
  localStorageToken,
  localStorageUser,
  theme1Id,
  authStorage
} from "../utils/variables";

import {
  useStoreMasterData,
  useStoreDetails,
  useStoreThemeData,
} from "../hooks/stores";
import { useStoreAdmins } from "../hooks/storeAdmins";
import CustomizeUploader from "../components/image-uploader/CustomizeUploader";

const CreateStore = () => {
  // Hooks

  const { pathname } = useLocation();

  const history = useHistory();

  const {
    register,
    formState: { errors },
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      storeAdministrator: {
        firstName: "",
        lastName: "",
        // profileImageUrl: "",
        email: "",
        password: "",
        mobile: "",
        secondaryEmail: "",
        firstTimeLogin: false,
        deleted: false,
        sysStoreAdminRole: {
          sysStoreAdminRoleId: 2,
        },
        sysStoreAdminStatus: {
          sysStoreAdminStatusId: 1,
        },
      },
      store: {
        // storeCode: "",
        storeName: "",
        storeDesc: "",
        // storeLogoUrl: "",
        storeLogoImage: null,
        metadata: "",
        seoTags: "",
        deleted: false,
        sysStoreStatus: {
          sysStoreStatusId: 1,
        },
        sysStoreType: {
          sysStoreTypeId: 3,
        },
        timeZone: "",
        theamId: theme1Id,
      },
    },
  });

  const { t } = useTranslation();

  const { mode } = useContext(WindmillContext);

  // States

  const [isLoading, setIsLoading] = useState(false);

  const [renderedToast, setRenderedToast] = useState(null);

  const loggedInUser = JSON.parse(authStorage.getItem(localStorageUser));

  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Queries

  const storeMasterData = useStoreMasterData();

  const storeDetails = useStoreDetails(
    loggedInUser?.storeId,
    pathname === "/edit-store" && !!loggedInUser.storeId
  );

  const storeAdmins = useStoreAdmins(
    loggedInUser?.storeId,
    pathname === "/edit-store" && !!loggedInUser.storeId
  );

  const storeThemeData = useStoreThemeData(loggedInUser?.storeId);

  // Effects

  // When store details come in edit mode
  // Fill the store info in form
  useEffect(() => {
    if (storeDetails.data) {
      reset((values) => ({
        ...values,
        store: {
          ...values.store,
          storeId: storeDetails.data.storeDetails.storeId,
          storeName: storeDetails.data.storeDetails.storeName,
          storeDesc: storeDetails.data.storeDetails.storeDesc,
          sysStoreStatus: {
            sysStoreStatusId:
              storeDetails.data.storeDetails.sysStoreStatus.sysStoreStatusId,
          },
          sysStoreType: {
            sysStoreTypeId:
              storeDetails.data.storeDetails.sysStoreType.sysStoreTypeId,
          },
          storeLogoUrl: storeDetails.data.storeDetails.storeLogoUrl,
          deleted: storeDetails.data.storeDetails.deleted,
          timeZone: storeDetails.data.storeDetails.timeZone,
          seoTags: storeDetails.data.storeDetails.seoTags,
          metadata: storeDetails.data.storeDetails.metadata,
        },
      }));
    }
  }, [storeDetails.data]);

  // When store admins details come
  // filter the super admin and fill the form
  useEffect(() => {
    if (storeAdmins.data) {
      const storeOwner = storeAdmins.data.storeAdminUsers?.filter(
        (admin) => admin.sysStoreAdminRole?.sysStoreAdminRoleId === 2
      )[0];
      if (storeOwner) {
        reset((values) => ({
          ...values,
          storeAdministrator: {
            ...values.storeAdministrator,
            firstName: storeOwner.firstName,
            lastName: storeOwner.lastName,
            profileImageUrl: storeOwner.profileImageUrl,
            email: storeOwner.email,
            password: "",
            mobile: storeOwner.mobile,
            secondaryEmail: storeOwner.secondaryEmail,
            firstTimeLogin: storeOwner.firstTimeLogin,
            deleted: storeOwner.deleted,
            sysStoreAdminRole: {
              sysStoreAdminRoleId:
                storeOwner.sysStoreAdminRole?.sysStoreAdminRoleId,
            },
            sysStoreAdminStatus: {
              sysStoreAdminStatusId:
                storeOwner.sysStoreAdminStatus?.sysStoreAdminStatusId,
            },
          },
        }));
      }
    }
  }, [storeAdmins.data]);

  // Methods

  const handleStoreSubmit = (values) => {
    const payload = { ...values };
    if (pathname === "/create-store") {
      // If a file is uploaded for create store
      if (uploadedFiles[0]) {
        var reader = new FileReader();
        reader.readAsDataURL(uploadedFiles[0]);
        reader.onloadend = function () {
          payload.store.storeLogoImage = reader.result;
          createStore.mutate(payload);
        };
      } else {
        createStore.mutate(payload);
      }
    } else {
      if (uploadedFiles[0]) {
        var reader = new FileReader();
        reader.readAsDataURL(uploadedFiles[0]);
        reader.onloadend = function () {
          payload.store.storeLogoImage = reader.result;
          delete payload.store.storeLogoUrl;
          updateStore.mutate(payload, {
            onSuccess: () => {
              history.push("/dashboard");
            },
          });
        };
      } else {
        updateStore.mutate(payload, {
          onSuccess: () => {
            history.push("/dashboard");
          },
        });
      }
    }
  };

  const onLogoUpload = (acceptedFiles) => {
    const reader = new FileReader();
    reader.readAsDataURL(acceptedFiles[0]);
    reader.onload = (e) => {
      const image = new Image();
      image.src = e.target.result;
      image.onload = () => {
        const { height, width } = image;
        if (height !== 60 || width !== 200) {
          setError("store.storeLogoImage", {
            type: "custom",
            message: t("logo-dimensions-error"),
          });
        } else {
          clearErrors("store.storeLogoImage");
          setUploadedFiles([
            Object.assign(acceptedFiles[0], {
              preview: URL.createObjectURL(acceptedFiles[0]),
            }),
          ]);
        }
      };
    };
  };

  // Mutations

  const updateStore = useMutation({
    mutationFn: async (values) => {
      const payload = { ...values };
      delete payload.storeAdministrator;

      const token = authStorage.getItem(localStorageToken);

      return await axios.put(
        `${process.env.REACT_APP_STORE_LOCATION_API}/stores`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onMutate: () => {
      setIsLoading(true);
      const toastId = toast.loading("Updating the store", {
        position: "top-center",
      });
      setRenderedToast(toastId);
    },
    onSuccess: () => {
      toast.update(renderedToast, {
        render: "Store updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
      });
    },
    onError: (error) => {
      toast.update(renderedToast, {
        render: getError(error),
        type: "error",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
      });
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const createStore = useMutation({
    mutationFn: async (values) => {
      console.log("inside create store mutation", values);
      return await axios.post(
        `${process.env.REACT_APP_STORE_LOCATION_API}/stores`,
        values
      );
    },
    onMutate: () => {
      setIsLoading(true);
      const toastId = toast.loading("Creating a store", {
        position: "top-center",
      });
      setRenderedToast(toastId);
    },
    onSuccess: () => {
      toast.update(renderedToast, {
        render: "Store created successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
      });
      history.push("/login");
    },
    onError: (error) => {
      toast.update(renderedToast, {
        render: getError(error),
        type: "error",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
      });
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  console.log("values inside create store", watch());

  console.log("uploaded files", uploadedFiles);

  console.log("errors in the create store file", errors);

  return (
    <div className="flex flex-col items-center min-h-screen p-6 bg-gray-50 dark:bg-gray-900">
      {/* <img src={Logo} alt="Logo" className="w-40 mt-10 mb-10" /> */}
      {mode === "dark" ? (
          <img src={DarkLogo} alt="Towne Logo" className="w-40 mt-10 mb-10" />
        ) : (
          <img src={LightLogo} alt="Towne Logo" className="w-40 mt-10 mb-10" />
        )}
      <div className="w-full max-w-4xl mx-auto">
        {pathname === "/edit-store" && (
          <Link to="/dashboard">
            <LabelArea
              label={
                <span className="mb-2 inline-block cursor-pointer">
                  {" "}
                  <IoArrowBack className="inline mr-2" />
                  <span className="underline">{t("dashboard")}</span>
                </span>
              }
            >
              {" "}
            </LabelArea>
          </Link>
        )}

        <div className="overflow-hidden bg-white rounded-lg shadow-md dark:bg-gray-800 relative">
          <div className="flex flex-col overflow-y-auto md:flex-row">
            <main className="flex items-center justify-center p-6 sm:p-12 w-full">
              <div className="w-full">
                <h1 className="mb-6 text-2xl font-semibold text-gray-700 dark:text-gray-200 text-center">
                  {t("store-info-heading")}
                </h1>
                <form onSubmit={handleSubmit(handleStoreSubmit)}>
                  <LabelArea label={t("store-name-label")} required />
                  <InputArea
                    register={register}
                    label={t("store-name-label")}
                    name="store.storeName"
                    type="text"
                    placeholder={t("store-name-placeholder")}
                    required
                    validations={validations.storeName}
                    className="w-full px-4 py-2 border rounded-md text-gray-700 bg-gray-100 focus:bg-white focus:ring-2 focus:ring-blue-500"
                  />
                  <Error errorName={errors.store?.storeName} />

                  <div className="mt-4">
                    <LabelArea label={t("store-description-label")} required />
                    <Textarea
                      label={t("store-description-label")}
                      placeholder={t("store-description-placeholder")}
                      {...register("store.storeDesc", {
                        required: {
                          value: true,
                          message: `${t("store-description-label")} ${t(
                            "required-error-message"
                          )}`,
                        },
                      })}
                      rows={5}
                      validations={validations.description}
                      className="w-full px-4 py-2 border rounded-md text-gray-700 bg-gray-100 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.store?.storeDesc ? (
                      <Error errorName={errors.store?.storeDesc} />
                    ) : (
                      <span className="text-sm mt-2 flex justify-between items-center text-gray-500">
                        <span className="flex items-center">
                          <GoAlert />{" "}
                          <span className="ml-2">
                            {t("description-char-limit")}
                          </span>
                        </span>
                        <span
                          className={descriptionCharLimitClass(
                            watch("store.storeDesc")
                          )}
                        >
                          {watch("store.storeDesc")?.length}
                        </span>
                      </span>
                    )}
                  </div>

                  <div className="mt-4">
                    <LabelArea label={t("timezone-label")} required />
                    <Select
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      {...register("store.timeZone", {
                        required: {
                          value: true,
                          message: t("timezone-required-error-message"),
                        },
                      })}
                    >
                      {timezones?.map((timezone) => (
                        <option
                          value={timezone.offset}
                        >{`${timezone.offset} (${timezone.name})`}</option>
                      ))}
                    </Select>
                    <Error errorName={errors.store?.timeZone} />
                  </div>
                  
                  {pathname === "/edit-store" && (
                  <div className="mt-4">
                    <LabelArea label={t("store-type-label")} required />
                    <Select
                      className="border h-12 text-sm focus:outline-none block w-full bg-gray-100 dark:bg-white border-transparent focus:bg-white"
                      {...register("store.sysStoreType.sysStoreTypeId", {
                        required: {
                          value: true,
                          message: t("store-type-error-message"),
                        },
                      })}
                    >
                      {storeMasterData.data?.storeTypes?.map((storeType) => (
                        <option value={storeType.sysStoreTypeId}>
                          {storeType.type}
                        </option>
                      ))}
                    </Select>
                    <Error
                      errorName={errors.store?.sysStoreType?.sysStoreTypeId}
                    />
                  </div>
                  )}

                  {/* <div>
                    {pathname === "/edit-store" &&
                      (watch("store.sysStoreType.sysStoreTypeId") == "1" ||
                        watch("store.sysStoreType.sysStoreTypeId") == "3") &&
                      storeThemeData.data && (
                        <LabelArea
                          label={
                            <span className="block mt-2">
                              {" "}
                              {t("store-url-label")} :{" "}
                              <a
                                href={`${process.env.REACT_APP_ONLINE_STORE_URL}/${loggedInUser?.storeId}`}
                                target="_blank"
                                className="underline pointer"
                              >{`${process.env.REACT_APP_ONLINE_STORE_URL}/${loggedInUser?.storeId}`}</a>
                            </span>
                          }
                        ></LabelArea>
                      )}
                  </div> */}

                  {pathname === "/edit-store" && (
                  <div className="mt-4">
                    <LabelArea label={t("logo-label")} />
                    <LogoUploader
                      imageUrl={watch("store.storeLogoUrl")}
                      files={uploadedFiles}
                      setFiles={setUploadedFiles}
                      warning={t("logo-upload-alert-message")}
                      onDrop={onLogoUpload}
                      error={errors?.store?.storeLogoImage}
                      isLogo
                      control={control}
                      field="store.storeLogoImage"
                      setValue={setValue}
                      //validations={{
                       // validate: (value, formValues) =>
                         // !value &&
                         // !formValues?.store?.storeLogoUrl
                          //  &&
                          // !uploadedFiles[0]
                          //   ? `${t("logo-label")} ${t(
                          //       "required-error-message"
                          //     )}`
                          //   : true,
                     // }}
                    />
                  </div>
                   )}
                   
                  {pathname === "/edit-store" && (
                    <div className="mt-4">
                      <LabelTextarea
                        label={t("seo-tags-label")}
                        placeholder={t("seo-tags-placeholder")}
                        register={register}
                        name="store.seoTags"
                      />
                      <Error errorName={errors.store?.seoTags} />
                    </div>
                  )}

                  {pathname === "/edit-store" && (
                    <div className="mt-4">
                      <LabelTextarea
                        label={t("meta-data-label")}
                        placeholder={t("meta-data-placeholder")}
                        register={register}
                        name="store.metadata"
                      />
                      <Error errorName={errors.store?.metadata} />
                    </div>
                  )}
                  {/* Remove Contact Info Section in edit section */}
                  {pathname !== "/edit-store" && (
                    <>
                      <hr className="my-10" />

                      <h1 className="mb-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">
                        {t("contact-info-heading")}
                      </h1>

                      <div className="flex">
                        <div className="w-1/2 mr-2">
                          <LabelArea label={t("first-name-label")} required />
                          <InputArea
                            register={register}
                            label={t("first-name-label")}
                            name="storeAdministrator.firstName"
                            type="text"
                            placeholder={t("first-name-placeholder")}
                            required
                            validations={validations.firstName}
                          />
                          <Error
                            errorName={errors.storeAdministrator?.firstName}
                          />
                        </div>

                        <div className="w-1/2 ml-2">
                          <LabelArea label={t("last-name-label")} required />
                          <InputArea
                            register={register}
                            label={t("last-name-label")}
                            name="storeAdministrator.lastName"
                            type="text"
                            placeholder={t("last-name-placeholder")}
                            required
                            validations={validations.lastName}
                          />
                          <Error
                            errorName={errors.storeAdministrator?.lastName}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <LabelArea label={t("email")} required />
                        <InputArea
                          register={register}
                          label={t("email")}
                          name="storeAdministrator.email"
                          type="email"
                          placeholder={t("email-placeholder")}
                          required
                          validations={validations.email}
                        />
                        <Error errorName={errors.storeAdministrator?.email} />
                      </div>

                      <div className="mt-4">
                        <LabelArea label={t("password")} required />
                        <InputArea
                          register={register}
                          label={t("password")}
                          name="storeAdministrator.password"
                          type="password"
                          placeholder="***************"
                          required
                          validations={validations.password}
                        />

                        {errors?.storeAdministrator?.password ? (
                          <Error
                            errorName={errors.storeAdministrator.password}
                          />
                        ) : (
                          <span className="text-sm mt-2 flex items-center text-gray-500">
                            <GoAlert />{" "}
                            <span className="ml-2">
                              {t("password-case-sensitive-alert-message")}
                            </span>
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  {pathname === "/edit-store" ? (
                    <div className="w-full grid gap-4 lg:gap-6 xl:gap-6 md:flex xl:flex border-t border-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 mt-16 rounded">
                      <div className="flex-grow-0 md:flex-grow lg:flex-grow xl:flex-grow">
                        <button
                          onClick={() => history.push("/dashboard")}
                          className="h-12 bg-gray-200 w-full text-red-500 hover:bg-red-50 hover:border-red-100 hover:text-red-600 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-red-700 border rounded"
                          layout="outline"
                        >
                          {t("cancel-btn-text")}
                        </button>
                      </div>
                      <div className="flex-grow-0 md:flex-grow lg:flex-grow xl:flex-grow">
                        <button
                          type="submit"
                          //className="w-full h-12"
                          className="align-bottom inline-flex items-center justify-center cursor-pointer leading-5 transition-colors duration-150 font-medium focus:outline-none px-4 py-2 text-sm text-white bg-gradient-to-b from-gradient-light-blue to-gradient-dark-blue border border-transparent active:bg-green-600 hover:bg-green-600 focus:ring focus:ring-purple-300 h-12 w-full rounded"
                          disabled={isLoading}
                        >
                          {t("update-store-cta")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      disabled={isLoading}
                      type="submit"
                      //className="mt-16 h-12 w-full"
                      className="align-bottom inline-flex items-center justify-center cursor-pointer leading-5 transition-colors duration-150 font-medium focus:outline-none px-4 py-2 text-sm text-white bg-gradient-to-b from-gradient-light-blue to-gradient-dark-blue border border-transparent active:bg-green-600 hover:bg-green-600 focus:ring focus:ring-purple-300 mt-16 h-12 w-full rounded"
                      block
                    >
                      {t("start-free-trial-cta")}

                    </button>
                  )}
                </form>

                {pathname === "/create-store" && (
                  <p className="mt-4">
                    <Link
                      className="text-sm font-medium text-green-color dark:text-green-color hover:underline"
                      to="/login"
                    >
                      {t("create-store-login-link")}
                    </Link>
                  </p>
                )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStore;
